import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { Search, Check, X, Eye, Loader2, CalendarX, Ban, Banknote, FileDown, ShieldCheck, ShieldAlert, Smartphone, Copy, ExternalLink, Plus, Send, RefreshCw, SlidersHorizontal, Settings2 } from 'lucide-react';
import {
  useBookings,
  useSesSubmit,
  useCreateBooking,
  useUpdateBooking,
  useCancelBooking,
  useConfirmTransfer,
  useActiveProperty,
  useBookingDetail,
  useDepositAction,
  useSubmitClaim,
  type Booking,
  type BookingStatus,
  type Channel,
  type Property,
  type DepositStatus,
  type DepositClaim,
  type BookingAgreement,
} from '../../../hooks/useManagement';
import PropertySelector from '../../../components/PropertySelector';
import { useConfirm } from '../../../components/ConfirmDialog';

type FilterKey = 'all' | BookingStatus;

const statusColors: Record<BookingStatus, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
  completed: 'bg-slate-100 text-slate-600 border-slate-200',
};
const channelColors: Record<Channel, string> = {
  direct: 'bg-teal-50 text-teal-700 border-teal-200',
  airbnb: 'bg-rose-50 text-rose-700 border-rose-200',
  booking: 'bg-blue-50 text-blue-700 border-blue-200',
  vrbo: 'bg-violet-50 text-violet-700 border-violet-200',
};

const eur = (n: number) => `€${n.toLocaleString('es-ES')}`;

// Optional columns for the bookings table (code+guest always shown, actions last).
// Mirrors the column picker of "Mis alojamientos" (kx_props_cols).
type BkColKey = 'property' | 'dates' | 'guests' | 'total' | 'status' | 'ses' | 'registration' | 'agreement' | 'notes';
const ALL_BK_COLS: { key: BkColKey; label: string }[] = [
  { key: 'property', label: 'Alojamiento' },
  { key: 'dates', label: 'Fechas' },
  { key: 'guests', label: 'Huéspedes' },
  { key: 'total', label: 'Total' },
  { key: 'status', label: 'Estado' },
  { key: 'ses', label: 'SES' },
  { key: 'registration', label: 'Check-in online' },
  { key: 'agreement', label: 'Acuerdo' },
  { key: 'notes', label: 'Notas' },
];
const DEFAULT_BK_COLS: BkColKey[] = ['property', 'dates', 'guests', 'total', 'status', 'ses'];
const BK_COLS_KEY = 'kx_bookings_cols';
const statusClass = (s: BookingStatus) => statusColors[s] ?? statusColors.completed;
const channelClass = (c: Channel) => channelColors[c] ?? channelColors.direct;

// Owner-facing label + badge styling for the automatic balance-charge status of a
// deposit booking. Returns null when there's nothing to show (no balance / fully paid).
type BalanceState = NonNullable<Booking['balance_charge_status']>;
const balanceLabels: Record<BalanceState, string> = {
  scheduled: 'Cobro automático programado',
  paid: 'Resto cobrado',
  failed: 'Cobro fallido — enviado enlace de pago',
  action_required: 'Pendiente de autorización del huésped',
};
const balanceBadge: Record<BalanceState, string> = {
  scheduled: 'bg-sky-50 text-sky-700 border-sky-200',
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed: 'bg-rose-50 text-rose-700 border-rose-200',
  action_required: 'bg-amber-50 text-amber-700 border-amber-200',
};
function balanceInfo(b: Booking): { label: string; cls: string } | null {
  // Show a status whenever there's a tracked balance charge OR an outstanding balance.
  const st = b.balance_charge_status;
  if (st) return { label: balanceLabels[st], cls: balanceBadge[st] };
  if ((b.balance_due ?? 0) > 0) return { label: 'Resto pendiente', cls: 'bg-slate-100 text-slate-600 border-slate-200' };
  return null;
}

// ── Damage deposit ────────────────────────────────────────────────────────────
const depositBadge: Record<DepositStatus, string> = {
  scheduled: 'bg-slate-100 text-slate-600 border-slate-200',
  held: 'bg-blue-50 text-blue-700 border-blue-200',
  released: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  claimed: 'bg-rose-50 text-rose-700 border-rose-200',
  action_required: 'bg-amber-50 text-amber-700 border-amber-200',
  failed: 'bg-rose-50 text-rose-700 border-rose-200',
};

function useDepositLabels(): Record<DepositStatus, string> {
  const { t } = useTranslation();
  return {
    scheduled: t('portal.manage.bookings.deposit.stScheduled', 'Programada'),
    held: t('portal.manage.bookings.deposit.stHeld', 'Retenida'),
    released: t('portal.manage.bookings.deposit.stReleased', 'Liberada'),
    claimed: t('portal.manage.bookings.deposit.stClaimed', 'Reclamada'),
    action_required: t('portal.manage.bookings.deposit.stActionRequired', 'Pendiente del huésped'),
    failed: t('portal.manage.bookings.deposit.stFailed', 'Fallida'),
  };
}

// SQLite timestamps come as "YYYY-MM-DD HH:MM:SS" (not strict ISO), so normalize
// before parsing instead of using parseISO.
function fmtTs(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? s : format(d, 'd MMM yyyy, HH:mm', { locale: es });
}

export default function ManageBookingsPage() {
  const { t } = useTranslation();
  const statusLabels: Record<BookingStatus, string> = {
    pending: t('portal.manage.status.pending'),
    confirmed: t('portal.manage.status.confirmed'),
    cancelled: t('portal.manage.status.cancelled'),
    completed: t('portal.manage.status.completed'),
  };
  const channelLabels: Record<Channel, string> = {
    direct: t('portal.manage.channel.direct'),
    airbnb: t('portal.manage.channel.airbnb'),
    booking: t('portal.manage.channel.booking'),
    vrbo: t('portal.manage.channel.vrbo'),
  };
  const statusOf = (b: Booking) => statusLabels[b.status] ?? b.status;
  const channelOf = (c: Channel) => channelLabels[c] ?? c;
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Booking | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  const confirm = useConfirm();

  const { propertyId, properties, allSelected, setPropertyId } = useActiveProperty();
  const hasProperties = properties.length > 0;
  // Single source of truth: fetch all (for the active property), filter client-side (gives accurate tab counts).
  const { data: bookings, isLoading } = useBookings('all', propertyId);

  // Deep-link from the dashboard/timeline: /portal/manage/bookings?open=<id> opens
  // that booking's detail modal. Force the "all properties" view first so the
  // target is in scope regardless of which property was previously selected.
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get('open');
  useEffect(() => {
    if (!openId) return;
    if (!allSelected) { setPropertyId(null); return; }
    if (!bookings) return;
    const b = bookings.find(x => String(x.id) === openId);
    if (b) setSelected(b);
    setSearchParams(prev => { prev.delete('open'); return prev; }, { replace: true });
  }, [openId, allSelected, bookings, setPropertyId, setSearchParams]);
  const updateBooking = useUpdateBooking();
  const cancelBooking = useCancelBooking();
  const confirmTransfer = useConfirmTransfer();
  // Extras / agreement / deposit / claims of the booking opened in the modal.
  const { data: detail } = useBookingDetail(selected?.id ?? null);

  const depositLabels = useDepositLabels();
  const depositBadgeLabel = t('portal.manage.bookings.deposit.badge', 'Fianza');
  const depositInfo = (b: Booking): { label: string; cls: string } | null => {
    if (!b.deposit_amount || b.deposit_amount <= 0 || !b.deposit_status) return null;
    return { label: `${depositBadgeLabel}: ${depositLabels[b.deposit_status]}`, cls: depositBadge[b.deposit_status] };
  };

  const setStatus = (id: number, status: 'confirmed' | 'cancelled') =>
    updateBooking.mutate({ id, status });

  // SES.Hospedajes — manual send / re-check of a booking's parte de viajeros.
  const sesSubmit = useSesSubmit();
  const handleSes = async (b: Booking, action: 'send' | 'check' = 'send') => {
    try {
      const r = await sesSubmit.mutateAsync({ booking_id: b.id, action });
      if (r.ok) setToast({ kind: 'success', msg: action === 'check' ? `${t('portal.manage.bookings.ses.statusIs', 'Estado SES')}: ${r.estado}` : t('portal.manage.bookings.ses.sentOk', 'Parte enviado a SES ✓') });
      else if (r.errors?.length) setToast({ kind: 'error', msg: r.errors.join(' ') });
      else {
        const known: Record<string, string> = {
          already_sent: t('portal.manage.bookings.ses.errAlreadySent', 'Esta reserva ya fue enviada a SES — no se reenvía para evitar un parte duplicado.'),
          password_undecryptable: t('portal.manage.bookings.ses.errPassword', 'La contraseña WS guardada ya no es válida. Vuelve a introducirla en Registro viajeros (SES).'),
          property_not_configured: t('portal.manage.bookings.ses.errNotConfigured', 'Este alojamiento no tiene cuenta SES ni código de establecimiento asignados.'),
          account_disabled: t('portal.manage.bookings.ses.errAccountDisabled', 'La cuenta SES asignada está desactivada o no existe.'),
          validation: t('portal.manage.bookings.ses.errValidation', 'Faltan datos de los viajeros.'),
        };
        setToast({ kind: 'error', msg: (r.error && known[r.error]) || r.descripcion || r.error || t('portal.manage.bookings.ses.errGeneric', 'Error al enviar a SES.') });
      }
    } catch { setToast({ kind: 'error', msg: t('portal.manage.bookings.ses.errNetwork', 'No se ha podido enviar a SES.') }); }
  };
  const sesBadge: Record<string, { label: string; cls: string }> = {
    sent: { label: t('portal.manage.bookings.ses.sent', 'Enviado'), cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    confirmed: { label: t('portal.manage.bookings.ses.confirmed', 'Confirmado'), cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    rejected: { label: t('portal.manage.bookings.ses.rejected', 'Rechazado'), cls: 'bg-rose-50 text-rose-700 border-rose-200' },
    error: { label: t('portal.manage.bookings.ses.error', 'Error'), cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  };

  // Column picker (persisted) — same UX as "Mis alojamientos".
  const [cols, setCols] = useState<BkColKey[]>(() => {
    try { const s = JSON.parse(localStorage.getItem(BK_COLS_KEY) || 'null'); return Array.isArray(s) && s.length ? s : DEFAULT_BK_COLS; } catch { return DEFAULT_BK_COLS; }
  });
  const [colsOpen, setColsOpen] = useState(false);
  const toggleCol = (k: BkColKey) => {
    const next = cols.includes(k) ? cols.filter(c => c !== k) : [...cols, k];
    setCols(next);
    try { localStorage.setItem(BK_COLS_KEY, JSON.stringify(next)); } catch { /* */ }
  };
  const colLabel = (k: BkColKey, fallback: string) => t(`portal.manage.bookings.col.${k}`, fallback);

  const isTransferPending = (b: Booking) => b.status === 'pending' && b.payment_method === 'transfer';

  const handleConfirmTransfer = async (b: Booking) => {
    const r = await confirm({
      title: t('portal.manage.bookings.confirmTransferTitle', 'Confirmar transferencia'),
      message: t('portal.manage.bookings.confirmTransferConfirm', '¿Confirmar que has recibido la transferencia de esta reserva? Se enviará la confirmación al huésped.'),
      confirmLabel: t('portal.manage.bookings.confirmTransferYes', 'Sí, he recibido el pago'),
    });
    if (r !== 'confirm') return;
    confirmTransfer.mutate(b.id, {
      onError: () => setToast({ kind: 'error', msg: t('portal.manage.bookings.confirmTransferFailed', 'No se ha podido confirmar la reserva.') }),
    });
  };

  // Owner-initiated cancellation. A confirmed (paid) booking can optionally be
  // refunded on the tenant's connected Stripe account.
  const handleCancel = async (b: Booking) => {
    const canRefund = b.status === 'confirmed';
    let refund = false;
    if (canRefund) {
      const r = await confirm({
        title: t('portal.manage.bookings.cancelTitle', 'Cancelar reserva'),
        message: t('portal.manage.bookings.cancelRefundBody', 'Esta reserva está pagada. ¿Quieres reembolsar el pago al huésped al cancelarla?'),
        confirmLabel: t('portal.manage.bookings.cancelAndRefund', 'Cancelar y reembolsar'),
        secondaryLabel: t('portal.manage.bookings.cancelNoRefund', 'Cancelar sin reembolso'),
        cancelLabel: t('portal.manage.bookings.back', 'Volver'),
        tone: 'danger',
      });
      if (r === 'cancel') return;
      refund = r === 'confirm';
    } else {
      const r = await confirm({
        title: t('portal.manage.bookings.cancelTitle', 'Cancelar reserva'),
        message: t('portal.manage.bookings.cancelConfirm', '¿Cancelar esta reserva?'),
        confirmLabel: t('portal.manage.bookings.cancelYes', 'Sí, cancelar'),
        cancelLabel: t('portal.manage.bookings.back', 'Volver'),
        tone: 'danger',
      });
      if (r !== 'confirm') return;
    }
    cancelBooking.mutate({ id: b.id, refund }, {
      onSuccess: (res) => {
        if (res.refund_error) {
          setToast({ kind: 'error', msg: t('portal.manage.bookings.refundError', 'La reserva se canceló, pero el reembolso falló. Emítelo manualmente desde Stripe.') });
        } else if (res.refunded) {
          setToast({ kind: 'success', msg: t('portal.manage.bookings.cancelledRefunded', 'Reserva cancelada y reembolsada.') });
        }
      },
      onError: () => setToast({ kind: 'error', msg: t('portal.manage.bookings.cancelFailed', 'No se ha podido cancelar la reserva.') }),
    });
  };

  const all = bookings ?? [];
  const countBy = (s: BookingStatus) => all.filter(b => b.status === s).length;

  const term = search.trim().toLowerCase();
  const filtered = all.filter(b => {
    const matchStatus = filter === 'all' || b.status === filter;
    const matchSearch =
      !term ||
      b.guest_name.toLowerCase().includes(term) ||
      (b.guest_email?.toLowerCase().includes(term) ?? false) ||
      (b.code?.toLowerCase().includes(term) ?? false);
    return matchStatus && matchSearch;
  });

  const tabs: { key: FilterKey; label: string; count: number }[] = [
    { key: 'all', label: t('portal.manage.bookings.tabAll'), count: all.length },
    { key: 'pending', label: t('portal.manage.bookings.tabPending'), count: countBy('pending') },
    { key: 'confirmed', label: t('portal.manage.bookings.tabConfirmed'), count: countBy('confirmed') },
    { key: 'completed', label: t('portal.manage.bookings.tabCompleted'), count: countBy('completed') },
    { key: 'cancelled', label: t('portal.manage.bookings.tabCancelled'), count: countBy('cancelled') },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {toast && (
        <div className={`fixed top-4 right-4 z-[110] rounded-xl px-4 py-3 text-sm font-medium shadow-lg border ${toast.kind === 'success' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-rose-50 text-rose-800 border-rose-200'}`} onClick={() => setToast(null)} role="status">
          {toast.msg}
        </div>
      )}
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('portal.manage.bookings.title')}</h1>
          <p className="text-sm text-slate-500 mt-1">{t('portal.manage.bookings.subtitle', { count: all.length })}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PropertySelector allowAll />
          {hasProperties && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white px-3.5 py-2 text-sm font-semibold transition-colors"
            >
              <Plus size={15} /> {t('portal.manage.bookings.newBooking', 'Nueva reserva')}
            </button>
          )}
        </div>
      </div>

      {showCreate && (
        <CreateBookingModal
          properties={properties}
          defaultPropertyId={propertyId}
          onClose={() => setShowCreate(false)}
        />
      )}

      {!hasProperties ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center text-slate-500">
          {t('portal.manage.selector.noPropertyHint', 'Primero crea un alojamiento para gestionar sus reservas.')}
        </div>
      ) : (
      /* Card */
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                filter === t.key
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t.label} <span className="text-slate-400">({t.count})</span>
            </button>
          ))}
        </div>

        {/* Search + column picker */}
        <div className="p-4 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          <div className="relative max-w-sm flex-1 min-w-[14rem]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder={t('portal.manage.bookings.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div className="relative ml-auto">
            <button onClick={() => setColsOpen(o => !o)} onBlur={() => setTimeout(() => setColsOpen(false), 150)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
              <SlidersHorizontal size={15} /> {t('portal.manage.bookings.columns', 'Columnas')}
            </button>
            {colsOpen && (
              <div className="absolute right-0 mt-1.5 z-30 w-56 rounded-xl border border-slate-200 bg-white shadow-xl py-1">
                {ALL_BK_COLS.map(c => (
                  <button key={c.key} onMouseDown={e => e.preventDefault()} onClick={() => toggleCol(c.key)}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 text-left">
                    <span className={`w-4 h-4 rounded border flex items-center justify-center ${cols.includes(c.key) ? 'bg-teal-600 border-teal-600 text-white' : 'border-slate-300'}`}>{cols.includes(c.key) && <Check size={12} />}</span>
                    {colLabel(c.key, c.label)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-teal-500" size={32} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <CalendarX className="text-slate-300 mb-3" size={40} />
            <p className="text-slate-600 font-medium">{t('portal.manage.bookings.emptyTitle')}</p>
            <p className="text-sm text-slate-400 mt-1">
              {term || filter !== 'all'
                ? t('portal.manage.bookings.emptyFiltered')
                : t('portal.manage.bookings.emptyNone')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.bookings.colCodeChannel')}</th>
                  <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.bookings.colGuest')}</th>
                  {ALL_BK_COLS.filter(c => cols.includes(c.key)).map(c => (
                    <th key={c.key} className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">{colLabel(c.key, c.label)}</th>
                  ))}
                  <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.bookings.colActions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(b => (
                  <tr key={b.id} onClick={() => setSelected(b)}
                    className="hover:bg-slate-50 transition-colors cursor-pointer">
                    <td className="px-5 py-3">
                      <div className="font-mono text-xs text-slate-600">{b.code ?? `#${b.id}`}</div>
                      <span className={`inline-flex items-center px-2 py-0.5 mt-1 rounded-full text-[11px] font-medium border ${channelClass(b.channel)}`}>
                        {channelOf(b.channel)}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="font-medium text-slate-900">{b.guest_name}</div>
                      {b.guest_country && <div className="text-xs text-slate-400">{b.guest_country}</div>}
                    </td>
                    {cols.includes('property') && (
                      <td className="px-5 py-3">
                        <div className="text-teal-700 text-xs font-medium truncate max-w-[14rem]" title={b.property_name ?? undefined}>{b.property_name ?? '—'}</div>
                      </td>
                    )}
                    {cols.includes('dates') && (
                      <td className="px-5 py-3">
                        <div className="text-slate-700">
                          {format(parseISO(b.check_in), 'd MMM', { locale: es })} – {format(parseISO(b.check_out), 'd MMM yyyy', { locale: es })}
                        </div>
                        <div className="text-xs text-slate-400">{t('portal.manage.bookings.nights', { count: b.nights })}</div>
                      </td>
                    )}
                    {cols.includes('guests') && <td className="px-5 py-3 text-slate-600">{b.guests}</td>}
                    {cols.includes('total') && (
                      <td className="px-5 py-3 font-semibold text-slate-900">
                        {eur(b.total)}
                        {(() => {
                          const bi = balanceInfo(b);
                          return bi ? (
                            <div className={`inline-flex items-center px-2 py-0.5 mt-1 rounded-full text-[11px] font-medium border ${bi.cls}`}>
                              {bi.label}
                            </div>
                          ) : null;
                        })()}
                        {(() => {
                          const di = depositInfo(b);
                          return di ? (
                            <div className={`inline-flex items-center px-2 py-0.5 mt-1 rounded-full text-[11px] font-medium border ${di.cls}`}>
                              {di.label}
                            </div>
                          ) : null;
                        })()}
                      </td>
                    )}
                    {cols.includes('status') && (
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusClass(b.status)}`}>
                          {statusOf(b)}
                        </span>
                      </td>
                    )}
                    {cols.includes('ses') && (
                      <td className="px-5 py-3" onClick={e => e.stopPropagation()}>
                        {b.ses_account_id == null ? (
                          <Link to="/portal/manage/ses"
                            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-teal-600"
                            title={t('portal.manage.bookings.ses.configureHint', 'Configura SES.Hospedajes para enviar el parte de viajeros')}>
                            <Settings2 className="w-3.5 h-3.5" /> {t('portal.manage.bookings.ses.configure', 'Configurar')}
                          </Link>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            {(() => {
                              const sb = b.ses_estado ? sesBadge[b.ses_estado] : undefined;
                              return sb ? <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${sb.cls}`}>{sb.label}</span> : null;
                            })()}
                            {(!b.ses_estado || b.ses_estado === 'rejected' || b.ses_estado === 'error') && (
                              <button onClick={() => handleSes(b, 'send')} disabled={sesSubmit.isPending}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 hover:bg-teal-200 text-teal-700 text-xs font-medium rounded-md transition-colors disabled:opacity-50"
                                title={b.ses_estado ? t('portal.manage.bookings.ses.resend', 'Reenviar a SES') : t('portal.manage.bookings.ses.send', 'Enviar parte a SES')}>
                                <Send className="w-3.5 h-3.5" /> SES
                              </button>
                            )}
                            {b.ses_estado === 'sent' && (
                              <button onClick={() => handleSes(b, 'check')} disabled={sesSubmit.isPending}
                                className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-md transition-colors disabled:opacity-50"
                                title={t('portal.manage.bookings.ses.checkStatus', 'Comprobar estado en SES')}>
                                <RefreshCw className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                    {cols.includes('registration') && (
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${b.registration_completed_at ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                          {b.registration_completed_at ? t('portal.manage.bookings.regDone', 'Completado') : t('portal.manage.bookings.regPending', 'Pendiente')}
                        </span>
                      </td>
                    )}
                    {cols.includes('agreement') && (
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${b.agreement_signed_at ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                          {b.agreement_signed_at ? t('portal.manage.bookings.agreementSigned', 'Firmado') : t('portal.manage.bookings.agreementPending', 'Pendiente')}
                        </span>
                      </td>
                    )}
                    {cols.includes('notes') && (
                      <td className="px-5 py-3">
                        <div className="text-xs text-slate-500 truncate max-w-[12rem]" title={b.notes ?? undefined}>{b.notes || '—'}</div>
                      </td>
                    )}
                    <td className="px-5 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {isTransferPending(b) && (
                          <button
                            onClick={() => handleConfirmTransfer(b)}
                            disabled={confirmTransfer.isPending}
                            className="p-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-md transition-colors disabled:opacity-50"
                            title={t('portal.manage.bookings.confirmTransfer', 'Confirmar (transferencia recibida)')}
                          >
                            <Banknote className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {b.status === 'pending' && !isTransferPending(b) && (
                          <>
                            <button
                              onClick={() => setStatus(b.id, 'confirmed')}
                              disabled={updateBooking.isPending}
                              className="p-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-md transition-colors disabled:opacity-50"
                              title={t('portal.manage.bookings.confirm')}
                            >
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => setStatus(b.id, 'cancelled')}
                              disabled={updateBooking.isPending}
                              className="p-1.5 bg-rose-100 hover:bg-rose-200 text-rose-700 rounded-md transition-colors disabled:opacity-50"
                              title={t('portal.manage.bookings.reject')}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                        {(b.status === 'pending' || b.status === 'confirmed') && (
                          <button
                            onClick={() => handleCancel(b)}
                            disabled={cancelBooking.isPending}
                            className="p-1.5 bg-rose-100 hover:bg-rose-200 text-rose-700 rounded-md transition-colors disabled:opacity-50"
                            title={b.status === 'confirmed'
                              ? t('portal.manage.bookings.cancelRefund', 'Cancelar y reembolsar')
                              : t('portal.manage.bookings.cancel', 'Cancelar reserva')}
                          >
                            <Ban className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => setSelected(b)}
                          className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md transition-colors"
                          title={t('portal.manage.bookings.viewDetail')}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div>
                <h3 className="font-bold text-slate-900 text-lg">{selected.code ?? `#${selected.id}`}</h3>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border mt-1 ${statusClass(selected.status)}`}>
                  {statusOf(selected)}
                </span>
              </div>
              <button onClick={() => setSelected(null)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-6 space-y-5 text-sm">
              <Section label={t('portal.manage.bookings.sectionGuest')}>
                <Row k={t('portal.manage.bookings.fieldName')} v={selected.guest_name} />
                <Row k={t('portal.manage.bookings.fieldEmail')} v={selected.guest_email ?? '—'} />
                <Row k={t('portal.manage.bookings.fieldPhone')} v={selected.guest_phone ?? '—'} />
                <Row k={t('portal.manage.bookings.fieldCountry')} v={selected.guest_country ?? '—'} />
              </Section>

              <Section label={t('portal.manage.bookings.sectionStay')}>
                <Row k={t('portal.manage.bookings.col.property', 'Alojamiento')} v={selected.property_name ?? '—'} />
                <Row k={t('portal.manage.bookings.fieldCheckIn')} v={format(parseISO(selected.check_in), 'EEEE d MMMM yyyy', { locale: es })} />
                <Row k={t('portal.manage.bookings.fieldCheckOut')} v={format(parseISO(selected.check_out), 'EEEE d MMMM yyyy', { locale: es })} />
                <Row k={t('portal.manage.bookings.fieldNights')} v={String(selected.nights)} />
                <Row k={t('portal.manage.bookings.fieldGuests')} v={String(selected.guests)} />
                <Row k={t('portal.manage.bookings.fieldChannel')} v={channelOf(selected.channel)} />
              </Section>

              <Section label={t('portal.manage.bookings.sectionPrice')}>
                <Row
                  k={t('portal.manage.bookings.priceLine', { nights: selected.nights, price: eur(selected.price_per_night) })}
                  v={eur(selected.nights * selected.price_per_night)}
                />
                <Row k={t('portal.manage.bookings.fieldCleaning')} v={eur(selected.cleaning_fee)} />
                {!!selected.tourist_tax && selected.tourist_tax > 0 && (
                  <Row k={t('portal.manage.bookings.fieldTouristTax', 'Tasa turística')} v={eur(selected.tourist_tax)} />
                )}
                <Row k={t('portal.manage.bookings.fieldTotal')} v={eur(selected.total)} bold />
                {!!selected.balance_due && selected.balance_due > 0 && (
                  <>
                    <Row k={t('portal.manage.bookings.fieldPaidOnline', 'Pagado online')} v={eur(selected.amount_paid ?? 0)} />
                    <Row k={t('portal.manage.bookings.fieldBalanceDue', 'Pendiente')} v={eur(selected.balance_due)} />
                  </>
                )}
                {(() => {
                  const bi = balanceInfo(selected);
                  return bi ? (
                    <div className="flex justify-between gap-4 items-center pt-1">
                      <span className="text-slate-500 shrink-0">{t('portal.manage.bookings.fieldBalanceStatus', 'Estado del resto')}</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${bi.cls}`}>
                        {bi.label}
                      </span>
                    </div>
                  ) : null;
                })()}
              </Section>

              {detail && detail.extras.length > 0 && (
                <Section label={t('portal.manage.bookings.sectionExtras', 'Extras')}>
                  {detail.extras.map(x => (
                    <Row key={x.id} k={`${x.name} ×${x.quantity}`} v={eur(x.total)} />
                  ))}
                </Section>
              )}

              {selected.notes && (
                <Section label={t('portal.manage.bookings.sectionNotes')}>
                  <p className="text-slate-600 bg-slate-50 rounded-lg p-3 text-xs leading-relaxed">{selected.notes}</p>
                </Section>
              )}

              {(selected as { guest_token?: string }).guest_token && (selected.status === 'confirmed' || selected.status === 'completed') && (
                <Section label={t('portal.manage.bookings.sectionGuestApp', 'App del huésped')}>
                  <p className="text-xs text-slate-500 mb-2">
                    {t('portal.manage.bookings.guestAppHint', 'Comparte este enlace con tu huésped: check-in online, contrato digital, guía de bienvenida y contacto contigo.')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={`https://guest.kanarix.es/g/${(selected as { guest_token?: string }).guest_token}`}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold"
                    >
                      <Smartphone className="w-3.5 h-3.5" /> {t('portal.manage.bookings.openGuestApp', 'Abrir app del huésped')}
                    </a>
                    <button
                      onClick={() => { navigator.clipboard?.writeText(`https://guest.kanarix.es/g/${(selected as { guest_token?: string }).guest_token}`); }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 text-xs font-semibold"
                    >
                      <Copy className="w-3.5 h-3.5" /> {t('portal.manage.bookings.copyGuestLink', 'Copiar enlace')}
                    </button>
                    <a
                      href={`/api/portal/manage/guest-id?booking_id=${selected.id}`}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 text-xs font-semibold"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> {t('portal.manage.bookings.viewGuestId', 'Ver documento de identidad')}
                    </a>
                  </div>
                </Section>
              )}

              {(selected.status === 'confirmed' || selected.status === 'completed') && (
                <Section label={t('portal.manage.bookings.sectionRegistration', 'Parte de viajeros')}>
                  {selected.registration_completed_at ? (
                    <p className="flex items-center gap-1.5 text-emerald-700 text-xs font-medium">
                      <Check className="w-3.5 h-3.5" /> {t('portal.manage.bookings.registrationDone', 'Registro de viajeros completado')}
                    </p>
                  ) : (
                    <p className="flex items-center gap-1.5 text-amber-600 text-xs font-medium">
                      <X className="w-3.5 h-3.5" /> {t('portal.manage.bookings.registrationPending', 'Registro de viajeros pendiente')}
                    </p>
                  )}
                  {selected.registration_completed_at && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      <a
                        href={`/api/portal/manage/guest-registration-export?booking_id=${selected.id}&format=csv`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold"
                      >
                        <FileDown className="w-3.5 h-3.5" /> {t('portal.manage.bookings.downloadRegistration', 'Descargar parte de viajeros (CSV)')}
                      </a>
                      <a
                        href={`/api/portal/manage/guest-registration-export?booking_id=${selected.id}&format=html`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 text-xs font-semibold"
                      >
                        <Eye className="w-3.5 h-3.5" /> {t('portal.manage.bookings.printRegistration', 'Ver / imprimir')}
                      </a>
                    </div>
                  )}
                </Section>
              )}

              {detail && <AgreementSection agreement={detail.agreement} />}

              {(() => {
                // Prefer the (fresher) detail payload: deposit status changes right
                // after a release / request / claim action.
                const bk = detail?.booking ?? selected;
                return (bk.deposit_amount ?? 0) > 0 ? (
                  <DepositSection booking={bk} claims={detail?.claims ?? []} />
                ) : null;
              })()}
            </div>

            {selected.status === 'pending' && (
              <div className="p-6 border-t border-slate-200 flex gap-3">
                {isTransferPending(selected) ? (
                  <button
                    onClick={() => { handleConfirmTransfer(selected); setSelected(null); }}
                    disabled={confirmTransfer.isPending}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Banknote className="w-4 h-4" /> {t('portal.manage.bookings.confirmTransfer', 'Confirmar (transferencia recibida)')}
                  </button>
                ) : (
                  <button
                    onClick={() => { setStatus(selected.id, 'confirmed'); setSelected(null); }}
                    disabled={updateBooking.isPending}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Check className="w-4 h-4" /> {t('portal.manage.bookings.confirmBooking')}
                  </button>
                )}
                <button
                  onClick={() => { handleCancel(selected); setSelected(null); }}
                  disabled={cancelBooking.isPending}
                  className="flex-1 bg-rose-50 hover:bg-rose-100 text-rose-700 font-medium py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <X className="w-4 h-4" /> {t('portal.manage.bookings.reject')}
                </button>
              </div>
            )}
            {selected.status === 'confirmed' && (
              <div className="p-6 border-t border-slate-200">
                <button
                  onClick={() => { handleCancel(selected); setSelected(null); }}
                  disabled={cancelBooking.isPending}
                  className="w-full bg-rose-50 hover:bg-rose-100 text-rose-700 font-medium py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Ban className="w-4 h-4" /> {t('portal.manage.bookings.cancelRefund', 'Cancelar y reembolsar')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{label}</h4>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ k, v, bold }: { k: string; v: string; bold?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500 shrink-0">{k}</span>
      <span className={`text-right ${bold ? 'font-bold text-slate-900' : 'text-slate-800'}`}>{v}</span>
    </div>
  );
}

// ── House-rules agreement (checkout acceptance record) ────────────────────────
function AgreementSection({ agreement }: { agreement: BookingAgreement | null }) {
  const { t } = useTranslation();
  return (
    <Section label={t('portal.manage.bookings.sectionAgreement', 'Normas de la casa')}>
      {agreement ? (
        <>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border bg-emerald-50 text-emerald-700 border-emerald-200">
            <Check className="w-3.5 h-3.5" /> {t('portal.manage.bookings.agreementAccepted', 'Normas aceptadas')}
          </span>
          <div className="pt-1.5 space-y-1.5">
            <Row k={t('portal.manage.bookings.agreementName', 'Aceptado por')} v={agreement.accepted_name} />
            <Row k={t('portal.manage.bookings.agreementDate', 'Fecha')} v={fmtTs(agreement.accepted_at)} />
            <Row k={t('portal.manage.bookings.agreementIp', 'Dirección IP')} v={agreement.ip_address ?? '—'} />
          </div>
          {agreement.rules_snapshot && (
            <details className="mt-1">
              <summary className="text-xs font-medium text-teal-600 hover:text-teal-700 cursor-pointer">
                {t('portal.manage.bookings.agreementShowRules', 'Ver normas aceptadas')}
              </summary>
              <p className="mt-2 text-xs text-slate-600 bg-slate-50 rounded-lg p-3 leading-relaxed whitespace-pre-line">
                {agreement.rules_snapshot}
              </p>
            </details>
          )}
        </>
      ) : (
        <p className="text-xs text-slate-400">{t('portal.manage.bookings.agreementNone', 'Sin aceptación registrada')}</p>
      )}
    </Section>
  );
}

// ── Damage deposit: status, release / request actions, damage claims ──────────
function DepositSection({ booking, claims }: { booking: Booking; claims: DepositClaim[] }) {
  const { t } = useTranslation();
  const depositLabels = useDepositLabels();
  const depositAction = useDepositAction();
  const claimMutation = useSubmitClaim();

  const [claimOpen, setClaimOpen] = useState(false);
  const [claimAmount, setClaimAmount] = useState('');
  const [claimDesc, setClaimDesc] = useState('');
  const [claimFiles, setClaimFiles] = useState<File[]>([]);
  const [claimError, setClaimError] = useState('');
  const [actionError, setActionError] = useState('');

  const amount = booking.deposit_amount ?? 0;
  const status = booking.deposit_status;

  const errorMessages: Record<string, string> = {
    amount_exceeds_deposit: t('portal.manage.bookings.deposit.errAmountExceeds', 'El importe supera la fianza retenida'),
    capture_failed: t('portal.manage.bookings.deposit.errCapture', 'No se pudo ejecutar el cargo en la tarjeta del huésped. Inténtalo de nuevo o contacta con el huésped.'),
    not_held: t('portal.manage.bookings.deposit.errNotHeld', 'La fianza ya no está retenida'),
    not_requestable: t('portal.manage.bookings.deposit.errNotRequestable', 'La fianza no se puede solicitar en su estado actual.'),
    no_deposit: t('portal.manage.bookings.deposit.errNoDeposit', 'Esta reserva no tiene fianza.'),
    release_failed: t('portal.manage.bookings.deposit.errRelease', 'No se pudo liberar la fianza. Inténtalo de nuevo.'),
    invalid_amount: t('portal.manage.bookings.deposit.errInvalidAmount', 'Indica un importe válido.'),
    description_required: t('portal.manage.bookings.deposit.errDescription', 'Describe los daños para reclamar la fianza.'),
    too_many_photos: t('portal.manage.bookings.deposit.errTooManyPhotos', 'Máximo 10 fotos.'),
    photo_too_large: t('portal.manage.bookings.deposit.errPhotoTooLarge', 'Cada foto debe pesar menos de 15 MB.'),
    invalid_photo_type: t('portal.manage.bookings.deposit.errPhotoType', 'Solo se admiten imágenes.'),
  };
  const friendly = (code: string) =>
    errorMessages[code] ?? t('portal.manage.bookings.deposit.errGeneric', 'Ha ocurrido un error. Inténtalo de nuevo.');

  const claimStatusLabels: Record<DepositClaim['status'], string> = {
    captured: t('portal.manage.bookings.deposit.claimCaptured', 'Cobrada'),
    failed: t('portal.manage.bookings.deposit.claimFailed', 'Fallida'),
    pending: t('portal.manage.bookings.deposit.claimPending', 'Pendiente'),
  };
  const claimStatusBadge: Record<DepositClaim['status'], string> = {
    captured: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    failed: 'bg-rose-50 text-rose-700 border-rose-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
  };

  const handleRelease = () => {
    if (!window.confirm(t('portal.manage.bookings.deposit.releaseConfirm', '¿Liberar la fianza ahora? La retención en la tarjeta del huésped se cancelará y ya no podrás reclamar daños.'))) return;
    setActionError('');
    depositAction.mutate(
      { bookingId: booking.id, action: 'release' },
      { onError: (e) => setActionError(friendly((e as Error).message)) },
    );
  };

  const handleRequest = () => {
    setActionError('');
    depositAction.mutate(
      { bookingId: booking.id, action: 'request' },
      { onError: (e) => setActionError(friendly((e as Error).message)) },
    );
  };

  const handlePhotos = (files: FileList | null) => {
    const list = Array.from(files ?? []);
    setClaimError(list.length > 10 ? friendly('too_many_photos') : '');
    setClaimFiles(list.slice(0, 10));
  };

  const handleSubmitClaim = () => {
    setClaimError('');
    const amt = Number(claimAmount);
    if (!Number.isFinite(amt) || amt <= 0) { setClaimError(friendly('invalid_amount')); return; }
    if (amt > amount) { setClaimError(friendly('amount_exceeds_deposit')); return; }
    if (!claimDesc.trim()) { setClaimError(friendly('description_required')); return; }
    claimMutation.mutate(
      { bookingId: booking.id, amount: amt, description: claimDesc.trim(), photos: claimFiles },
      {
        onSuccess: () => {
          setClaimOpen(false);
          setClaimAmount(''); setClaimDesc(''); setClaimFiles([]);
        },
        onError: (e) => setClaimError(friendly((e as Error).message)),
      },
    );
  };

  return (
    <>
      <Section label={t('portal.manage.bookings.sectionDeposit', 'Fianza')}>
        <div className="flex justify-between gap-4 items-center">
          <span className="text-slate-500 shrink-0">{t('portal.manage.bookings.deposit.amount', 'Importe')}</span>
          <span className="font-bold text-slate-900">{eur(amount)}</span>
        </div>
        {status && (
          <div className="flex justify-between gap-4 items-center">
            <span className="text-slate-500 shrink-0">{t('portal.manage.bookings.deposit.status', 'Estado')}</span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${depositBadge[status]}`}>
              {depositLabels[status]}
            </span>
          </div>
        )}
        {booking.deposit_held_at && (
          <Row k={t('portal.manage.bookings.deposit.heldAt', 'Retenida el')} v={fmtTs(booking.deposit_held_at)} />
        )}
        {booking.deposit_released_at && (
          <Row k={t('portal.manage.bookings.deposit.releasedAt', 'Liberada el')} v={fmtTs(booking.deposit_released_at)} />
        )}
        {booking.deposit_last_error && (
          <p className="text-[11px] text-rose-600">{booking.deposit_last_error}</p>
        )}

        {status === 'held' && (
          <div className="flex flex-wrap gap-2 pt-1.5">
            <button
              onClick={handleRelease}
              disabled={depositAction.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50 transition-colors"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> {t('portal.manage.bookings.deposit.release', 'Liberar fianza ahora')}
            </button>
            {!claimOpen && (
              <button
                onClick={() => { setClaimOpen(true); setClaimError(''); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-semibold transition-colors"
              >
                <ShieldAlert className="w-3.5 h-3.5" /> {t('portal.manage.bookings.deposit.claim', 'Reclamar daños')}
              </button>
            )}
          </div>
        )}
        {(status === 'scheduled' || status === 'action_required' || status === 'failed') && (
          <div className="pt-1.5">
            <button
              onClick={handleRequest}
              disabled={depositAction.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold disabled:opacity-50 transition-colors"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> {t('portal.manage.bookings.deposit.request', 'Solicitar fianza ahora')}
            </button>
          </div>
        )}
        {actionError && <p className="text-xs text-rose-600 pt-1">{actionError}</p>}

        {/* Damage claim form */}
        {claimOpen && status === 'held' && (
          <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2.5">
            <div>
              <label className="block text-xs text-slate-500 mb-1">{t('portal.manage.bookings.deposit.claimAmount', 'Importe a reclamar (€)')}</label>
              <input
                type="number" min={0} max={amount} step="0.01"
                value={claimAmount}
                onChange={e => setClaimAmount(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <p className="text-[11px] text-slate-400 mt-0.5">{t('portal.manage.bookings.deposit.claimMax', 'Máx. {{max}} €', { max: amount.toLocaleString('es-ES') })}</p>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">{t('portal.manage.bookings.deposit.claimDesc', 'Descripción de los daños')}</label>
              <textarea
                value={claimDesc}
                onChange={e => setClaimDesc(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">{t('portal.manage.bookings.deposit.claimPhotos', 'Fotos de los daños (opcional, máx. 10)')}</label>
              <input
                type="file" accept="image/*" multiple
                onChange={e => handlePhotos(e.target.files)}
                className="w-full text-xs text-slate-600 file:mr-2 file:rounded-lg file:border-0 file:bg-slate-200 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-300"
              />
              {claimFiles.length > 0 && (
                <p className="text-[11px] text-slate-400 mt-0.5">
                  {t('portal.manage.bookings.deposit.claimPhotosSelected', '{{count}} foto(s) seleccionada(s)', { count: claimFiles.length })}
                </p>
              )}
            </div>
            {claimError && <p className="text-xs text-rose-600">{claimError}</p>}
            <div className="flex gap-2">
              <button
                onClick={handleSubmitClaim}
                disabled={claimMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold disabled:opacity-50 transition-colors"
              >
                {claimMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {claimMutation.isPending
                  ? t('portal.manage.bookings.deposit.claimSubmitting', 'Ejecutando el cargo…')
                  : t('portal.manage.bookings.deposit.claimSubmit', 'Reclamar fianza')}
              </button>
              <button
                onClick={() => { setClaimOpen(false); setClaimError(''); }}
                disabled={claimMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold disabled:opacity-50 transition-colors"
              >
                {t('portal.manage.bookings.deposit.claimCancel', 'Cancelar')}
              </button>
            </div>
          </div>
        )}
      </Section>

      {claims.length > 0 && (
        <Section label={t('portal.manage.bookings.sectionClaims', 'Reclamaciones de fianza')}>
          {claims.map(c => (
            <div key={c.id} className="rounded-lg bg-slate-50 p-3 space-y-1.5">
              <div className="flex items-center justify-between gap-3">
                <span className="font-bold text-slate-900">{eur(c.amount)}</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${claimStatusBadge[c.status]}`}>
                  {claimStatusLabels[c.status] ?? c.status}
                </span>
              </div>
              <p className="text-[11px] text-slate-400">{fmtTs(c.created_at)}</p>
              {c.description && <p className="text-xs text-slate-600 leading-relaxed">{c.description}</p>}
              {c.files.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {c.files.map(f => (
                    <a
                      key={f.id}
                      href={`/api/portal/manage/claim-file?id=${f.id}`}
                      target="_blank" rel="noreferrer"
                      title={f.filename ?? undefined}
                    >
                      <img
                        src={`/api/portal/manage/claim-file?id=${f.id}`}
                        alt={f.filename ?? t('portal.manage.bookings.deposit.claimPhotoAlt', 'Foto de los daños')}
                        className="h-16 w-16 object-cover rounded-lg border border-slate-200"
                        loading="lazy"
                      />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </Section>
      )}
    </>
  );
}

/* ── Manual booking creation ────────────────────────────────────────────────── */
function CreateBookingModal({ properties, defaultPropertyId, onClose }: {
  properties: Property[];
  defaultPropertyId: number | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateBooking();
  const [err, setErr] = useState<string | null>(null);
  const [f, setF] = useState({
    property_id: defaultPropertyId ?? properties[0]?.id ?? 0,
    guest_name: '', guest_email: '', guest_phone: '',
    check_in: '', check_out: '', guests: 2,
    price_per_night: 0, cleaning_fee: 0,
    status: 'confirmed' as BookingStatus,
    channel: 'direct' as Channel,
    notes: '',
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF(s => ({ ...s, [k]: v }));

  const nights = f.check_in && f.check_out
    ? Math.max(0, Math.round((new Date(f.check_out).getTime() - new Date(f.check_in).getTime()) / 864e5))
    : 0;
  const total = nights * (Number(f.price_per_night) || 0) + (Number(f.cleaning_fee) || 0);

  // Prefill the nightly price from the selected property.
  const onProperty = (id: number) => {
    set('property_id', id);
    const p = properties.find(x => x.id === id);
    if (p?.base_price && !f.price_per_night) set('price_per_night', p.base_price);
    if (p?.cleaning_fee && !f.cleaning_fee) set('cleaning_fee', p.cleaning_fee);
  };

  const submit = () => {
    setErr(null);
    if (!f.property_id) { setErr(t('portal.manage.bookings.errProperty', 'Selecciona un alojamiento.')); return; }
    if (f.guest_name.trim().length < 2) { setErr(t('portal.manage.bookings.errGuest', 'Indica el nombre del huésped.')); return; }
    if (!f.check_in || !f.check_out || nights < 1) { setErr(t('portal.manage.bookings.errDates', 'Indica fechas válidas (mínimo 1 noche).')); return; }
    create.mutate({
      property_id: f.property_id, guest_name: f.guest_name.trim(),
      guest_email: f.guest_email.trim() || null, guest_phone: f.guest_phone.trim() || null,
      check_in: f.check_in, check_out: f.check_out, guests: Number(f.guests) || 1,
      nights, price_per_night: Number(f.price_per_night) || 0,
      cleaning_fee: Number(f.cleaning_fee) || 0, total,
      status: f.status, channel: f.channel, notes: f.notes.trim() || null,
    }, {
      onSuccess: onClose,
      onError: () => setErr(t('portal.manage.bookings.errCreate', 'No se ha podido crear la reserva.')),
    });
  };

  const inputCls = 'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500';
  const labelCls = 'block text-xs font-semibold text-slate-500 mb-1';

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900">{t('portal.manage.bookings.newBooking', 'Nueva reserva')}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className={labelCls}>{t('portal.manage.bookings.fieldProperty', 'Alojamiento')}</label>
            <select value={f.property_id} onChange={e => onProperty(Number(e.target.value))} className={inputCls}>
              {properties.map(p => <option key={p.id} value={p.id}>{p.name || `#${p.id}`}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className={labelCls}>{t('portal.manage.bookings.fieldName')}</label>
              <input value={f.guest_name} onChange={e => set('guest_name', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input type="email" value={f.guest_email} onChange={e => set('guest_email', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{t('portal.manage.bookings.fieldPhone', 'Teléfono')}</label>
              <input value={f.guest_phone} onChange={e => set('guest_phone', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Check-in</label>
              <input type="date" value={f.check_in} onChange={e => set('check_in', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Check-out</label>
              <input type="date" value={f.check_out} min={f.check_in} onChange={e => set('check_out', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{t('portal.manage.bookings.fieldGuests')}</label>
              <input type="number" min={1} value={f.guests} onChange={e => set('guests', Number(e.target.value))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{t('portal.manage.bookings.fieldChannel', 'Canal')}</label>
              <select value={f.channel} onChange={e => set('channel', e.target.value as Channel)} className={inputCls}>
                <option value="direct">Directa</option>
                <option value="airbnb">Airbnb</option>
                <option value="booking">Booking</option>
                <option value="vrbo">Vrbo</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>{t('portal.manage.bookings.fieldPricePerNight', '€/noche')}</label>
              <input type="number" min={0} value={f.price_per_night} onChange={e => set('price_per_night', Number(e.target.value))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{t('portal.manage.bookings.fieldCleaning', 'Limpieza €')}</label>
              <input type="number" min={0} value={f.cleaning_fee} onChange={e => set('cleaning_fee', Number(e.target.value))} className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>{t('portal.manage.bookings.fieldStatus', 'Estado')}</label>
              <select value={f.status} onChange={e => set('status', e.target.value as BookingStatus)} className={inputCls}>
                <option value="confirmed">Confirmada</option>
                <option value="pending">Pendiente</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className={labelCls}>{t('portal.manage.bookings.fieldNotes', 'Notas')}</label>
              <textarea value={f.notes} onChange={e => set('notes', e.target.value)} rows={2} className={inputCls} />
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 px-4 py-3 text-sm flex items-center justify-between">
            <span className="text-slate-500">{t('portal.manage.bookings.nights', { count: nights })}</span>
            <span className="font-bold text-slate-900">{eur(total)}</span>
          </div>
          {err && <p className="text-sm text-rose-600">{err}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
              {t('portal.manage.bookings.cancel', 'Cancelar')}
            </button>
            <button onClick={submit} disabled={create.isPending}
              className="px-4 py-2 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold disabled:opacity-50">
              {create.isPending ? t('portal.manage.bookings.creating', 'Creando…') : t('portal.manage.bookings.create', 'Crear reserva')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
